/**
 * One agent, one project (#219).
 *
 * An onboarding agent's runtime config — its Core/Telemetry modules,
 * Databricks catalog, Genie space, site prompt — belongs to the ONE project
 * its service was registered on (org `registered_service.project_id`). On
 * 2026-09-22 `mineone` was pointed at MineZero's agent as a stopgap and
 * MineOne trainees were served MineZero's data with no warning anywhere.
 *
 * The module now refuses binding an agent that already serves another
 * project (`set_project_agent`). What the module cannot see is the org's
 * service registry, so the "registered for another site" half lives here:
 * the picker disables those agents and flags a current binding that is
 * already wrong.
 *
 * Pure: every input is plain data so the rules are unit-tested.
 */

export type SiteRef = {
  /** org project id, as a string (Site.id). */
  id: string;
  name: string;
  /** MineTwin project key (Site.projectName) — what project_agent keys on. */
  projectName: string;
};

export type BindingRef = {
  /** MineTwin project key. */
  projectId: string;
  identityHex: string;
  displayName: string;
};

/** From org `registered_service`, keyed by normalized identity hex. */
export type RegisteredAgentInfo = {
  /** org project id; 0 = an org-scoped service with no project. */
  orgProjectId: number;
  serviceName: string;
};

export type AgentConflict =
  | { kind: 'serves-other'; siteNames: string[] }
  | { kind: 'registered-elsewhere'; siteName: string };

export function normalizeHex(hex: string): string {
  return hex.trim().toLowerCase().replace(/^0x/, '');
}

function siteNameByKey(key: string, sites: readonly SiteRef[]): string {
  return sites.find((s) => s.projectName === key)?.name ?? key;
}

function siteNameByOrgId(orgId: number, sites: readonly SiteRef[]): string {
  return sites.find((s) => s.id === String(orgId))?.name ?? `another project (#${orgId})`;
}

/**
 * Why `identityHex` must not serve `site`, or null when it may.
 * "Serves another project" wins: the module refuses that one outright.
 * An agent missing from the registry (not visible, or not a registered
 * service) is allowed — the module's own guard still applies.
 */
export function agentConflict(args: {
  identityHex: string;
  site: SiteRef;
  bindings: readonly BindingRef[];
  registered: ReadonlyMap<string, RegisteredAgentInfo>;
  sites: readonly SiteRef[];
}): AgentConflict | null {
  const hex = normalizeHex(args.identityHex);
  const elsewhere = args.bindings
    .filter((b) => b.projectId !== args.site.projectName && normalizeHex(b.identityHex) === hex)
    .map((b) => siteNameByKey(b.projectId, args.sites));
  if (elsewhere.length > 0) {
    return { kind: 'serves-other', siteNames: [...new Set(elsewhere)].sort() };
  }
  const reg = args.registered.get(hex);
  if (reg && reg.orgProjectId !== 0 && String(reg.orgProjectId) !== args.site.id) {
    return { kind: 'registered-elsewhere', siteName: siteNameByOrgId(reg.orgProjectId, args.sites) };
  }
  return null;
}

/** Short suffix for a dropdown option. */
export function conflictLabel(c: AgentConflict): string {
  return c.kind === 'serves-other'
    ? `serves ${c.siteNames.join(', ')}`
    : `registered for ${c.siteName}`;
}

/** The sentence under the picker when the CURRENT binding is already wrong. */
export function currentBindingWarning(c: AgentConflict, siteName: string): string {
  const where =
    c.kind === 'serves-other' ? `also serves ${c.siteNames.join(', ')}` : `is registered for ${c.siteName}`;
  return `This agent ${where}, so ${siteName} trainees get that site's data, catalog and site prompt. Pick ${siteName}'s own agent.`;
}

/**
 * What the all-sites list says for a site's agent. `visible_project_agents`
 * only covers the viewer's minetwin scope, so a missing row is "no agent"
 * only where the viewer is known to be in scope (an active membership);
 * anywhere else it may just be out of view (#219 review).
 */
export function siteAgentText(binding: { displayName: string } | undefined, viewerIsMember: boolean): string {
  if (binding) return binding.displayName;
  return viewerIsMember ? 'no agent' : 'no agent visible to you';
}
